import React, { useState, useRef, useEffect } from "react";
import {
    Box,
    Button,
    MenuItem,
    Select,
    Popover,
    TextField,
    Stack,
} from "@mui/material";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";

const OPTIONS = [
    "Last 30 days",
    "Last 90 days",
    "Today",
    "Yesterday",
    "This Month",
    "Last Month",
    "Any time",
    "Date Range",
];

// -------- PRESET RANGE CALCULATOR --------
const getPresetRange = (type) => {
    const today = moment().startOf("day");

    switch (type) {
        case "Today":
            return { from: today, to: today };

        case "Yesterday":
            return {
                from: today.clone().subtract(1, "day"),
                to: today.clone().subtract(1, "day"),
            };

        case "Last 30 days":
            return {
                from: today.clone().subtract(29, "days"),
                to: today,
            };

        case "Last 90 days":
            return {
                from: today.clone().subtract(89, "days"),
                to: today,
            };

        case "This Month":
            return {
                from: today.clone().startOf("month"),
                to: today.clone().endOf("month"),
            };

        case "Last Month":
            return {
                from: today.clone().subtract(1, "month").startOf("month"),
                to: today.clone().subtract(1, "month").endOf("month"),
            };

        case "Any time":
            return { from: null, to: null };

        default:
            return null;
    }
};

// -------- LABEL FORMATTER --------
const formatRangeLabel = (start, end) => {
    if (!start || !end) return "Date Range";
    return `${moment(start).format("DD MMM YYYY")} - ${moment(end).format(
        "DD MMM YYYY"
    )}`;
};

export default function TimePeriodSelector({ activeItem, onChange }) {
    const [selected, setSelected] = useState(activeItem);

    const [startInput, setStartInput] = useState("");
    const [endInput, setEndInput] = useState("");


    // universal internal range state (Date objects)
    const [range, setRange] = useState([null, null]);
    const [startDate, endDate] = range;

    const [anchorEl, setAnchorEl] = useState(null);
    const selectRef = useRef(null);

    useEffect(() => {
        if (startDate) {
            setStartInput(moment(startDate).format("DD-MM-YYYY"));
        }
        if (endDate) {
            setEndInput(moment(endDate).format("DD-MM-YYYY"));
        }
    }, [startDate, endDate]);


    // -------- HANDLE SELECT CHANGE --------
    const handleSelect = (value) => {
        setSelected(value);

        // Handle preset
        const preset = getPresetRange(value);
        if (!preset) return;

        // Update internal range
        setRange([
            preset.from ? preset.from.toDate() : null,
            preset.to ? preset.to.toDate() : null,
        ]);

        // Emit consistent output
        onChange({
            rangeType: value,
            from: preset.from ? preset.from.format("YYYY-MM-DD") : null,
            to: preset.to ? preset.to.format("YYYY-MM-DD") : null,
        });
    };

    const handleMenuItemClick = (item) => {
        if (item === "Date Range") {
            setSelected(item);

            if (!range[0] || !range[1]) {
                const today = moment().startOf("day").toDate();
                setRange([today, today]);
            }

            setAnchorEl(selectRef.current);
        }
    }

    // -------- APPLY CUSTOM RANGE --------
    const handleApply = () => {
        setAnchorEl(null);
        setSelected("Date Range");

        onChange({
            rangeType: "Date Range",
            from: moment(startDate).format("YYYY-MM-DD"),
            to: moment(endDate).format("YYYY-MM-DD"),
        });
    };

    // -------- CANCEL CUSTOM RANGE --------
    const handleCancel = () => {
        setAnchorEl(null);
        // on cancel, don't clear date; just close
        // User may reopen for editing the previous values
    };

    const handleStartInput = (value) => {
        setStartInput(value);

        const parsed = moment(value, "DD-MM-YYYY", true);
        if (parsed.isValid()) {
            setRange([parsed.toDate(), endDate]);
        }
    };

    const handleEndInput = (value) => {
        setEndInput(value);

        const parsed = moment(value, "DD-MM-YYYY", true);
        if (parsed.isValid()) {
            setRange([startDate, parsed.toDate()]);
        }
    };


    return (
        <>
            {/* SELECT DROPDOWN */}
            <Select
                value={selected}
                onChange={(e) => handleSelect(e.target.value)}
                size="small"
                inputRef={selectRef}
                sx={{ minWidth: 240, height: 40, backgroundColor: 'white' }}
                renderValue={(val) => {
                    if (val === "Date Range") {
                        return formatRangeLabel(range[0], range[1]);
                    }
                    return val;
                }}
            >
                {OPTIONS.map((item) => (
                    <MenuItem key={item} value={item}
                        onClick={() => { handleMenuItemClick(item) }}
                    >
                        {item}
                    </MenuItem>
                ))}
            </Select>

            {/* DATE RANGE POPOVER */}
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
                <Box p={2} minWidth={360}>
                    {/* FROM + TO Inputs */}
                    <Stack direction="row" spacing={2} mb={2}>
                        <TextField
                            label="From"
                            size="small"
                            placeholder="DD-MM-YYYY"
                            value={startInput}
                            onChange={(e) => handleStartInput(e.target.value)}
                        />

                        <TextField
                            label="To"
                            size="small"
                            placeholder="DD-MM-YYYY"
                            value={endInput}
                            onChange={(e) => handleEndInput(e.target.value)}
                        />


                    </Stack>

                    {/* DATE_PICKER */}
                    <DatePicker
                        selected={startDate}
                        onChange={(update) => {
                            setRange(update);
                        }}
                        startDate={startDate}
                        endDate={endDate}
                        selectsRange
                        inline
                        monthsShown={2}
                    />

                    {/* BUTTONS */}
                    <Stack direction="row" justifyContent="flex-end" spacing={1} mt={2}>
                        <Button onClick={handleCancel}>Cancel</Button>
                        <Button
                            variant="contained"
                            disabled={!startDate || !endDate}
                            onClick={handleApply}
                        >
                            Apply
                        </Button>
                    </Stack>
                </Box>
            </Popover>
        </>
    );
}
